import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@stores/useAuthStore';
import { useShallow } from 'zustand/react/shallow';
import {
    broadcastOAuthConnectionEvent,
    getCommunityIdFromOAuthReturnPath,
} from '@lib/oauthConnectionEvents';

/**
 * Handles callback parameters from the X/Twitter OAuth flow.
 * On mount it reads `x_status` params, refreshes the profile on success, shows a toast, and cleans the URL.
 */
export function useXCallbackEffect() {
    const {
        isAuthenticated,
        refreshUserProfile,
    } = useAuthStore(useShallow(s => ({
        isAuthenticated: s.isAuthenticated,
        refreshUserProfile: s.refreshUserProfile,
    })));

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const status = params.get('x_status'); // 'success' | 'error' | null
        if (!status) {
            return;
        }

        const username = params.get('x_username');
        const reason = params.get('x_reason');
        const communityId = getCommunityIdFromOAuthReturnPath(window.location.pathname);

        broadcastOAuthConnectionEvent({
            provider: 'x',
            status: status === 'success' ? 'success' : 'error',
            communityId,
            username: username ?? undefined,
            reason: reason ?? undefined,
        });

        const cleanParams = () => {
            params.delete('x_status');
            params.delete('x_username');
            params.delete('x_reason');

            const remaining = params.toString();
            const cleanUrl = remaining
                ? `${window.location.pathname}?${remaining}`
                : window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        };

        (async () => {
            if (status === 'success') {
                if (!isAuthenticated) {
                    cleanParams();
                    return;
                }

                try {
                    await refreshUserProfile();
                    const description = username
                        ? `Account linked as @${username}. You can return to the original tab.`
                        : 'You can return to the original tab.';
                    toast.success('Connected to X', { description });
                } catch (error) {
                    console.error('Failed to refresh profile after X connect', error);
                }
            } else {
                //TODO log error
                const isAuthFailure = reason === 'auth_failed';
                const description = isAuthFailure ? undefined : (reason || 'Please try connecting again.');
                const message = isAuthFailure ? 'X connection failed! try again!' : 'X connection failed';
                toast.error(message, { description });
            }

            cleanParams();
        })();
    }, [isAuthenticated, refreshUserProfile]);
}
